import { Box, Button, IconButton, TextField } from '@mui/material';
import {
  Criteria,
  CreateScreenerResult,
  SavedScreener,
} from '../../../types/screener.type';
import { MarketCriteriaItem } from '../Criteria/MarketCriteria/MarketCriteria';
import { SectorCriteriaItem } from '../Criteria/SectorCriteria/SectorCriteria';
import { useState } from 'react';
import { ReactComponent as ClearIcon } from '../../../assets/icons/close-circle-fill-grey.svg';
import '../../Common/typography.css';
import './ScreenerSaveDialog.css';
import { MAX_SAVED_SCREENERS } from '../../../constants/screener/screener.constant';

type DialogMode = 'save-new' | 'save-changes' | 'confirm' | 'limit';

interface ScreenerSaveDialogProps {
  currentScreenerId: string | null;
  currentPresetId: string | null;
  setCurrentScreenerId: (currentScreenerId: string) => void;
  savedScreeners: SavedScreener[];
  setOpenSaveDialog: (openSaveDialog: boolean) => void;
  screenerName: string;
  criteria: Criteria[];
  markets: MarketCriteriaItem[];
  sectors: SectorCriteriaItem[];
  onCreateScreener: (
    name: string,
    criteria: Criteria[],
    markets: MarketCriteriaItem[],
    sectors: SectorCriteriaItem[],
  ) => CreateScreenerResult;
  onUpdateScreener: (
    id: string,
    name: string,
    criteria: Criteria[],
    markets: MarketCriteriaItem[],
    sectors: SectorCriteriaItem[],
  ) => boolean;
  setSkipApiCall: (skipApiCall: boolean) => void;
}

const ScreenerSaveDialog = ({
  currentScreenerId,
  currentPresetId,
  savedScreeners,
  setOpenSaveDialog,
  screenerName,
  criteria,
  markets,
  sectors,
  onCreateScreener,
  onUpdateScreener,
  setSkipApiCall,
}: ScreenerSaveDialogProps) => {
  const editingExisting =
    !currentPresetId &&
    !!currentScreenerId &&
    savedScreeners.some((s) => s.id === currentScreenerId);

  const atSavedLimit = savedScreeners.length >= MAX_SAVED_SCREENERS;

  const [mode, setMode] = useState<DialogMode>(() => {
    if (editingExisting) return 'save-changes';
    if (atSavedLimit) return 'limit';
    return 'save-new';
  });
  const [inputValue, setInputValue] = useState(
    editingExisting ? screenerName : '',
  );
  const [inputError, setInputError] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  // Skip filtering while an IME composition (e.g. Vietnamese Telex) is in progress, or it eats characters.
  const [isComposing, setIsComposing] = useState(false);

  const isDuplicateName = (value: string) => {
    const trimmed = value.trim().toLowerCase();
    if (!trimmed) return false;
    // Only exclude the currently-open screener from the duplicate check when
    // editing it in place (save-changes) - a "Save New" copy must be checked
    // against every existing name, including the one it was copied from.
    const excludeId = mode === 'save-changes' ? currentScreenerId : null;
    return savedScreeners.some(
      (s) => s.id !== excludeId && s.name.trim().toLowerCase() === trimmed,
    );
  };

  const validate = (value: string): boolean => {
    if (value.length > 24) return true;
    if (isDuplicateName(value)) return true;
    return false;
  };

  const helperText = (() => {
    if (!inputError) return '';
    if (inputValue.length > 24) {
      return 'Error! Max 24 characters';
    }
    return 'Error! This name is already in use';
  })();

  const handleClear = () => {
    setInputError(false);
    setInputValue('');
  };

  const handleChange = (event: any) => {
    const value = event.target.value;
    setInputValue(value);
    if (!isComposing) {
      setInputError(validate(value));
    }
  };

  const handleCompositionStart = () => setIsComposing(true);

  const handleCompositionEnd = (event: any) => {
    setIsComposing(false);
    setInputError(validate(event.target.value));
  };

  const handleCreateNew = () => {
    const result = onCreateScreener(
      inputValue.trim(),
      criteria,
      markets,
      sectors,
    );
    if (!result.success) {
      if (result.reason === 'limit') {
        setMode('limit');
      }
      return;
    }
    setSkipApiCall(true);
    setConfirmMessage('Your strategy has been saved.');
    setMode('confirm');
  };

  const handleSaveChanges = () => {
    if (!currentScreenerId) return;
    const success = onUpdateScreener(
      currentScreenerId,
      inputValue.trim(),
      criteria,
      markets,
      sectors,
    );
    if (!success) return;
    setConfirmMessage('Changes has been saved.');
    setMode('confirm');
  };

  const handleSwitchToSaveNew = () => {
    if (atSavedLimit) {
      setMode('limit');
      return;
    }
    setInputValue('');
    setInputError(false);
    setMode('save-new');
  };

  const isSaveDisabled = inputError || !inputValue.trim();

  const renderSaveNew = () => (
    <>
      <Box className="screener-save-dialog-header">
        <p className="screener-save-dialog-title">Save new strategy</p>
      </Box>
      <Box
        className="screener-save-dialog-input-wrapper"
        style={{ height: inputError ? '66px' : '48px' }}
      >
        <TextField
          className="screener-save-dialog-input"
          size="small"
          placeholder="Name your screener"
          value={inputValue}
          onChange={handleChange}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          error={inputError}
          helperText={helperText}
          slotProps={{
            input: {
              endAdornment: inputValue && inputValue.length > 0 && (
                <IconButton onClick={handleClear} edge="end" size="small">
                  <ClearIcon />
                </IconButton>
              ),
            },
          }}
        />
      </Box>
      <Box className="screener-save-dialog-actions">
        <Button
          className="screener-advanced-criteria-popup-button"
          variant="contained"
          color="primary"
          onClick={() => setOpenSaveDialog(false)}
        >
          Cancel
        </Button>
        <Button
          className="screener-advanced-criteria-popup-button screener-advanced-criteria-popup-button-get-results"
          variant="contained"
          color="primary"
          onClick={handleCreateNew}
          disabled={isSaveDisabled}
        >
          Save
        </Button>
      </Box>
    </>
  );

  const renderSaveChanges = () => (
    <>
      <Box className="screener-save-dialog-header">
        <p className="screener-save-dialog-title">Save changes?</p>
      </Box>
      <Box className="screener-save-dialog-input-wrapper">
        <p className="screener-save-dialog-readonly-name">{screenerName}</p>
      </Box>
      <Box className="screener-save-dialog-actions screener-save-dialog-actions--three">
        <Button
          className="screener-save-dialog-compact-button"
          onClick={() => setOpenSaveDialog(false)}
        >
          Cancel
        </Button>
        <Button
          className="screener-save-dialog-compact-button"
          onClick={handleSwitchToSaveNew}
        >
          Save New
        </Button>
        <Button
          className="screener-save-dialog-compact-button screener-save-dialog-compact-button--primary"
          onClick={handleSaveChanges}
        >
          Save
        </Button>
      </Box>
    </>
  );

  const renderConfirm = () => (
    <>
      <Box className="screener-save-dialog-confirm">
        <p className="screener-save-dialog-confirm-text body-m-bold">
          {confirmMessage}
        </p>
      </Box>
      <Box className="screener-save-dialog-actions">
        <Button
          className="screener-advanced-criteria-popup-button screener-advanced-criteria-popup-button-get-results screener-save-dialog-ok-button"
          variant="contained"
          color="primary"
          onClick={() => setOpenSaveDialog(false)}
        >
          OK
        </Button>
      </Box>
    </>
  );

  const renderLimit = () => (
    <>
      <Box className="screener-save-dialog-confirm">
        <p className="screener-save-dialog-confirm-text body-m-bold">
          You&apos;ve reached the 10-strategy limit. Delete an existing
          strategy to add a new one.
        </p>
      </Box>
      <Box className="screener-save-dialog-actions">
        <Button
          className="screener-advanced-criteria-popup-button screener-advanced-criteria-popup-button-get-results screener-save-dialog-ok-button"
          variant="contained"
          color="primary"
          onClick={() => setOpenSaveDialog(false)}
        >
          OK
        </Button>
      </Box>
    </>
  );

  return (
    <Box className="screener-save-dialog">
      {mode === 'save-new' && renderSaveNew()}
      {mode === 'save-changes' && renderSaveChanges()}
      {mode === 'confirm' && renderConfirm()}
      {mode === 'limit' && renderLimit()}
    </Box>
  );
};

export default ScreenerSaveDialog;
